import React, { useMemo } from 'react';
import { useHistory } from 'react-router';
import styled, { css } from 'styled-components';
import { useQuery } from 'apollo-3';

import { getGuidedPractices } from 'GraphQL/query/GuidedPractice.query';
import { PATHS } from 'Routes/Main/paths';
import { Box, ButtonFilled, SvgIcon, Text } from 'Components/Base';

const StyledButton = styled(ButtonFilled)`
  ${({ theme }) => css`
    background-color: ${theme.colors.guidedPractice.goToGuidedPracticeButtonBackground};
    width: fit-content;
    color: ${theme.colors.guidedPractice.goToGuidedPracticeButtonText};
    border: 0;
    display: flex;
    align-items: center;
    gap: ${theme.space[2]}px;
    padding: ${theme.space[2]}px ${theme.space[4]}px;
    border-radius: 100px;
    font-weight: 700;
    position: relative;
  `}
`;

const RedBadge = styled(Box)`
  ${({ theme, size }) => css`
    background-color: ${theme.colors.badge};
    border-radius: 50%;
    position: absolute;
    right: 14%;
    top: 14%;
    transform: scale(1) translate(140%, -35%);
    transform-origin: 100% 0%;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    min-width: ${size}px;
    min-height: ${size}px;
  `}
`;

const GoToGuidedPracticeButton = ({ ...props }) => {
  const history = useHistory();
  const { data, loading: loadingGuidedPractices } = useQuery(getGuidedPractices);

  const guidedPractices = useMemo(() => data?.guidedPractices ?? [], [data]);

  const notCompletedAmount = useMemo(
    () =>
      guidedPractices.filter(
        ({ status }) => status === 'IN_PROGRESS' || status === 'NOT_STARTED' || status === null
      ).length,
    [guidedPractices]
  );

  const hasGuidedPractice = useMemo(() => guidedPractices.length > 0, [guidedPractices]);

  const badgeSize = useMemo(() => (notCompletedAmount > 99 ? 29 : 24), [notCompletedAmount]);

  if (!hasGuidedPractice || loadingGuidedPractices) return null;

  return (
    <StyledButton onClick={() => history.push(PATHS.guidedPractice)} {...props}>
      {notCompletedAmount > 0 && (
        <RedBadge size={badgeSize}>
          <Text>{notCompletedAmount > 99 ? '99+' : notCompletedAmount}</Text>
        </RedBadge>
      )}
      <SvgIcon icon={SvgIcon.ICON_LIST.QUESTION_MARK_PERSON_ICON} size={24} />
      Guided Practice
    </StyledButton>
  );
};

export default GoToGuidedPracticeButton;
